---
label: Primer Design
category: analysis
icon: FlaskConicalIcon
keywords: [primer, design, pcr, tm, melting temperature, primer3, wasm, gc]
addedInVersion: "0.0.0"
---

# Primer Design

Design PCR primers for your sequences directly in Gnomux.

## Starting a Design

Open a sequence and click the **Design Primers** button in the toolbar. This
opens the design dialog where you configure parameters and choose an engine.

## Engines

Two primer design engines are available:

| Engine     | Runs            | Sign-in  | Notes                                  |
| ---------- | --------------- | -------- | -------------------------------------- |
| **Local**  | In your browser | Not required | Uses WebAssembly. Fast and works offline. |
| **Primer3**| Server-side     | Required | Industry-standard Primer3 engine.      |

Local is selected by default. Both engines accept the same design parameters and
return results in the same format. They may produce slightly different primer
candidates because they use independent scoring implementations.

## Parameters

Adjust these settings in the design dialog before running:

- **Product Size** — min and max amplicon length in base pairs.
- **Number of Primer Pairs** — how many candidate pairs to return.
- **Max Degeneracy** — maximum fold-degeneracy allowed in each primer. Set to 1
  to require exact sequences only.
- **Primer Length** — min, optimal, and max primer size in nucleotides.
- **Melting Temperature** — min, optimal, and max Tm in degrees Celsius.
- **GC Content** — min and max GC percentage.
- **Concentrations** — salt (mM) and DNA (nM) concentrations used for Tm
  calculation.

Click **Reset to Defaults** to restore all parameters to their starting values.
Your settings are saved automatically and persist across sessions.

## Results

After designing, results appear as a table of primer pairs showing:

- Left and right primer sequences
- Melting temperature (Tm) for each primer
- GC content percentage
- Position on the template
- Degeneracy fold (if applicable)
- Product size in base pairs

## Primer Properties

Each primer is evaluated on several thermodynamic and structural properties:

- **Tm (melting temperature)** — the temperature at which half the primer
  molecules are annealed to the template. Primers in a pair should have similar
  Tm values.
- **GC%** — the fraction of G and C bases. Typically 40-60% for good primers.
- **Self-complementarity** — tendency of a primer to form dimers with itself.
  Lower scores are better.
- **Hairpin** — tendency to fold back on itself. Lower scores reduce the risk of
  secondary structure interfering with annealing.
- **Pair complementarity** — tendency of the forward and reverse primers to
  anneal to each other instead of the template.

## Saving Primers

Click **Save** next to any primer pair to add both the forward and reverse
primers to your primer library. Saved primers appear in the **Primers** tab of
the sequence viewer.
